import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, ShoppingBag, CreditCard } from "lucide-react";
import Brand from "../components/Brand";

// FEATURE FLAG — Stripe Connect Express requires Mike to complete the "Go Live" KYC
// on his new Stripe Connect account. Until then, members fall back to pasting their
// own PayPal / Cash App / Square / Stripe links on each product. Flip to `true` when
// Mike has finished Stripe's "Go Live" step + sent the live Connect key.
const CONNECT_ENABLED = false;

/**
 * StripeConnectCard — drop-in card showing Connect status + the right CTA.
 * Used at the top of MyStoreTab and MyCoursesTab.
 *
 * States:
 *   not_connected      → orange "Set Up Payments" button → opens Stripe onboarding
 *   pending            → yellow "Complete Stripe Setup" button → resumes onboarding
 *   connected          → green "Connected · Payouts Active" + "Manage" link
 */
export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/stripe/status");
      setStatus(r.data);
    } catch (e) {
      toast.error("Couldn't check Stripe status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // While Connect is paused, skip the network call entirely
    if (!CONNECT_ENABLED) return;
    refresh();
    // If just returned from Stripe, re-poll status after a brief delay
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_return") || params.get("stripe_refresh")) {
      setTimeout(refresh, 2000);
      const url = new URL(window.location.href);
      url.searchParams.delete("stripe_return");
      url.searchParams.delete("stripe_refresh");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While Connect is paused, render a helpful fallback notice instead of trying to onboard
  if (!CONNECT_ENABLED) {
    return (
      <div className="rounded-xl bg-white border-2 border-[#F39A1F] p-4 md:p-5" data-testid="stripe-connect-paused">
        <div className="flex items-start gap-3">
          <div className="bg-[#F39A1F]/20 rounded-lg p-2 shrink-0"><CreditCard className="w-5 h-5 text-gold-deep"/></div>
          <div className="flex-1">
            <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ PAYMENTS · USE YOUR OWN PAYMENT LINK</div>
            <div className="font-bold text-foreground text-base mt-0.5">Paste your PayPal / Cash App / Square / Stripe link on each product.</div>
            <p className="text-sm text-foreground/80 mt-1">Buyers click "Buy" → go straight to your payment page → money lands in your account. <strong>You keep 100%.</strong> Direct one-click Stripe Checkout is in the works — coming soon.</p>
          </div>
        </div>
      </div>
    );
  }

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const r = await api.post("/me/stripe/onboard", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      // The classic restricted-key permissions error — give Mike a friendly heads-up
      if (detail.toLowerCase().includes("permission")) {
        toast.error("Stripe Connect permissions aren't enabled yet. Please contact Mike at team@faitharama.com — he needs to flip a switch on the platform side.");
      } else {
        toast.error(detail || "Couldn't start Stripe setup. Try again in a moment.");
      }
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const r = await api.post("/me/stripe/dashboard-link");
      window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't open dashboard");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="rounded-xl bg-foreground/5 p-4 text-sm text-foreground/60">Checking your payment setup…</div>;
  if (!status) return null;

  // CONNECTED & READY
  if (status.charges_enabled && status.payouts_enabled) {
    return (
      <div className="rounded-xl bg-gradient-to-r from-[#E8F5E9] to-[#F1F8E9] border-2 border-[#22693C] p-4 md:p-5" data-testid="stripe-connect-active">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="bg-[#22693C]/15 rounded-lg p-2 shrink-0"><CheckCircle2 className="w-5 h-5 text-[#22693C]"/></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-wider-cap font-bold text-[#22693C]">★ STRIPE CONNECTED · PAYOUTS ACTIVE</div>
            <div className="font-bold text-foreground text-base mt-0.5">Money flows directly to your bank.</div>
            <p className="text-sm text-foreground/80 mt-1">Every sale on your /u/store page is paid into your Stripe account immediately. <Brand/> takes 0% — your $49/mo IS the platform fee.</p>
          </div>
          <button onClick={openDashboard} disabled={busy} data-testid="open-stripe-dashboard" className="shrink-0 inline-flex items-center gap-1.5 bg-white border-2 border-[#22693C] text-[#22693C] hover:bg-[#22693C] hover:text-white px-3 py-2 rounded-md font-bold uppercase tracking-wider-cap text-[10px] transition">
            Manage Stripe <ExternalLink className="w-3 h-3"/>
          </button>
        </div>
      </div>
    );
  }

  // PARTIALLY ONBOARDED (account exists but not finished)
  if (status.connected && status.details_submitted && !status.charges_enabled) {
    return (
      <div className="rounded-xl bg-gradient-to-r from-white to-[#FBE9A0] border-2 border-[#F39A1F] p-4 md:p-5" data-testid="stripe-connect-pending">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="bg-[#F39A1F]/20 rounded-lg p-2 shrink-0"><RefreshCw className="w-5 h-5 text-gold-deep"/></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ STRIPE REVIEW IN PROGRESS</div>
            <div className="font-bold text-foreground text-base mt-0.5">Stripe is verifying your details.</div>
            <p className="text-sm text-foreground/80 mt-1">Usually takes 1–2 business days. We'll auto-light up your "Buy" buttons the moment Stripe approves you. No action needed.</p>
          </div>
          <button onClick={refresh} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 bg-white border-2 border-[#F39A1F] text-gold-deep hover:bg-[#F39A1F] hover:text-[#0F172A] px-3 py-2 rounded-md font-bold uppercase tracking-wider-cap text-[10px] transition" data-testid="refresh-stripe-status">
            <RefreshCw className="w-3 h-3"/> Refresh
          </button>
        </div>
      </div>
    );
  }

  // INCOMPLETE ONBOARDING (account created but member never finished the form)
  if (status.connected && !status.details_submitted) {
    return (
      <div className="rounded-xl bg-gradient-to-r from-white to-[#FBE9A0] border-2 border-[#F39A1F] p-4 md:p-5" data-testid="stripe-connect-incomplete">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="bg-[#F39A1F]/20 rounded-lg p-2 shrink-0"><AlertTriangle className="w-5 h-5 text-gold-deep"/></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ FINISH STRIPE SETUP</div>
            <div className="font-bold text-foreground text-base mt-0.5">You started — let's finish so you can get paid.</div>
            <p className="text-sm text-foreground/80 mt-1">Stripe needs a few more details (bank routing, tax ID, ID photo). Takes 5–10 minutes. Once done, every sale flows to your bank automatically.</p>
          </div>
          <button onClick={startOnboarding} disabled={busy} data-testid="resume-stripe-onboarding" className="shrink-0 bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-4 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs transition shadow inline-flex items-center gap-1.5">
            {busy ? "Loading…" : <><ShoppingBag className="w-3.5 h-3.5"/> Resume Stripe Setup</>}
          </button>
        </div>
      </div>
    );
  }

  // NOT STARTED (default)
  return (
    <div className="rounded-xl bg-gradient-to-br from-[#0F172A] to-[#2A2521] text-white p-5 md:p-6 border-2 border-[#F39A1F]/40" data-testid="stripe-connect-not-set">
      <div className="grid md:grid-cols-[1fr_auto] gap-4 md:items-center">
        <div>
          <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F] mb-1">★ SET UP PAYMENTS · ONE-TIME</div>
          <h3 className="font-serif-display text-xl md:text-2xl uppercase leading-tight">Connect Stripe — Get Paid Direct.</h3>
          <p className="text-white/80 text-sm mt-2 max-w-2xl">When buyers click "Buy" on your /u/store page, money flows <strong className="text-[#F39A1F]">straight to your bank account</strong>. <Brand/> takes 0% — every dollar is yours. Setup takes 5–10 min in Stripe (bank, ID, tax info). One-time, then never again.</p>
        </div>
        <button onClick={startOnboarding} disabled={busy} data-testid="start-stripe-onboarding" className="bg-[#F39A1F] hover:bg-white text-[#0F172A] px-5 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs transition shadow-lg inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
          {busy ? "Loading…" : <><ShoppingBag className="w-4 h-4"/> Set Up Payments →</>}
        </button>
      </div>
    </div>
  );
}
